import { useState } from "react";
import { Check, ChevronsUpDown, ChevronLeft, ChevronRight, CalendarDays, Minus, Plus } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./ui/command";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { currentPeriod, periodLabel, shiftPeriod } from "../lib/format";

export const AnakCombobox = ({ anak, value, onChange, testid, allowAll = false, placeholder = "Pilih anak" }) => {
  const [open, setOpen] = useState(false);
  const selected = anak.find((a) => a.id === value);
  const pick = (id) => { onChange(id); setOpen(false); };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" data-testid={testid} className="h-10 w-full justify-between bg-white font-normal">
          <span className={`truncate ${selected ? "text-gray-900" : "text-gray-500"}`}>
            {selected ? selected.nama : allowAll ? "Semua anak" : placeholder}
          </span>
          <ChevronsUpDown className="h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[240px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Cari nama anak..." data-testid={`${testid}-search`} />
          <CommandList>
            <CommandEmpty>Anak tidak ditemukan.</CommandEmpty>
            <CommandGroup>
              {allowAll && (
                <CommandItem value="__semua__" onSelect={() => pick("")} data-testid={`${testid}-option-all`} className="py-3">
                  Semua anak
                </CommandItem>
              )}
              {anak.map((a) => (
                <CommandItem key={a.id} value={`${a.nama} ${a.id}`} onSelect={() => pick(a.id)} data-testid={`${testid}-option-${a.id}`} className="py-3">
                  <Check className={`mr-2 h-4 w-4 ${value === a.id ? "opacity-100" : "opacity-0"}`} />
                  <span className="flex-1 truncate">{a.nama}</span>
                  {!a.aktif && <span className="text-xs text-gray-400">nonaktif</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export const PerusahaanSelect = ({ perusahaan, value, onChange, testid, allowAll = false, allowNone = false }) => (
  <Select value={value || (allowAll ? "all" : allowNone ? "none" : undefined)} onValueChange={(v) => onChange(v === "all" || v === "none" ? "" : v)}>
    <SelectTrigger data-testid={testid} className="h-10 bg-white">
      <SelectValue placeholder="Pilih perusahaan" />
    </SelectTrigger>
    <SelectContent>
      {allowAll && <SelectItem value="all">Semua perusahaan</SelectItem>}
      {allowNone && <SelectItem value="none">Tanpa perusahaan</SelectItem>}
      {perusahaan.map((p) => (
        <SelectItem key={p.id} value={p.id} data-testid={`${testid}-option-${p.id}`}>{p.nama}</SelectItem>
      ))}
    </SelectContent>
  </Select>
);

export const KategoriSelect = ({ value, onChange, testid, allowAll = false }) => (
  <Select value={value || (allowAll ? "all" : undefined)} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
    <SelectTrigger data-testid={testid} className="h-10 bg-white"><SelectValue placeholder="Pilih kategori" /></SelectTrigger>
    <SelectContent>
      {allowAll && <SelectItem value="all">Semua kategori</SelectItem>}
      <SelectItem value="gold" data-testid={`${testid}-option-gold`}>Gold</SelectItem>
      <SelectItem value="platinum" data-testid={`${testid}-option-platinum`}>Platinum</SelectItem>
      {allowAll && <SelectItem value="belum" data-testid={`${testid}-option-belum`}>Belum diatur</SelectItem>}
    </SelectContent>
  </Select>
);

export const Stepper = ({ value, onChange, testid, min = 0, invalid = false, disabled = false, className = "w-36" }) => {
  const n = /^\d+$/.test(value) ? parseInt(value, 10) : 0;
  return (
    <div className={`flex h-10 shrink-0 items-stretch overflow-hidden rounded-lg border bg-white ${className} ${disabled ? "opacity-40" : ""} ${invalid ? "border-red-500 ring-2 ring-red-200" : n > 0 ? "border-primary" : "border-input"}`}>
      <button type="button" disabled={disabled} data-testid={`${testid}-minus`} onClick={() => onChange(String(Math.max(min, n - 1)))} className="w-11 text-gray-600 hover:bg-gray-100 active:scale-95" aria-label="Kurangi"><Minus className="mx-auto h-4 w-4" /></button>
      <input data-testid={`${testid}-input`} disabled={disabled} inputMode="numeric" value={value} onChange={(e) => onChange(e.target.value.trim().slice(0, 4))} onFocus={(e) => e.target.select()}
        className={`w-full min-w-0 border-x border-input bg-transparent text-center font-mono text-lg font-semibold outline-none ${n > 0 ? "text-primary" : "text-gray-400"}`} />
      <button type="button" disabled={disabled} data-testid={`${testid}-plus`} onClick={() => onChange(String(n + 1))} className="w-11 text-gray-600 hover:bg-gray-100 active:scale-95" aria-label="Tambah"><Plus className="mx-auto h-4 w-4" /></button>
    </div>
  );
};

export const RupiahInput = ({ value, onChange, testid, ...props }) => (
  <div className="relative">
    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 font-mono text-sm text-gray-500">Rp</span>
    <Input
      inputMode="numeric"
      data-testid={testid}
      className="h-10 bg-white pl-10 font-mono"
      value={value ? Number(value).toLocaleString("id-ID") : ""}
      onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 12))}
      {...props}
    />
  </div>
);

export const PeriodPicker = ({ value, onChange, testid = "period" }) => (
  <div data-testid={testid} className="flex h-11 items-center gap-0.5 rounded-lg border border-gray-200 bg-white px-0.5 md:h-10">
    <Button type="button" variant="ghost" size="icon" className="h-9 w-9 shrink-0" data-testid={`${testid}-prev-btn`} onClick={() => onChange(shiftPeriod(value, -1))} aria-label="Periode sebelumnya">
      <ChevronLeft className="h-5 w-5" />
    </Button>
    <div className="flex min-w-0 flex-1 items-center justify-center gap-2 px-2 text-center">
      <CalendarDays className="hidden h-4 w-4 shrink-0 text-gray-400 sm:block" />
      <span data-testid={`${testid}-label`} className="truncate text-[15px] font-semibold text-gray-900">{periodLabel(value)}</span>
    </div>
    <Button type="button" variant="ghost" size="icon" className="h-9 w-9 shrink-0" data-testid={`${testid}-next-btn`} onClick={() => onChange(shiftPeriod(value, 1))} aria-label="Periode berikutnya">
      <ChevronRight className="h-5 w-5" />
    </Button>
    {value !== currentPeriod() && (
      <Button type="button" variant="ghost" className="h-9 shrink-0 px-2.5 text-[13px] font-semibold text-primary" data-testid={`${testid}-today-btn`} onClick={() => onChange(currentPeriod())}>
        Kini
      </Button>
    )}
  </div>
);
